import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { useApp } from '@/store/AppStore';
import { useCatalogVersion } from '@/lib/catalogStore';

const navLinks = [
  { en: 'Featured', ko: '인기 상품', to: '/#featured' },
  { en: 'Blog', ko: 'AI 소식', to: '/blog' },
  { en: 'How It Works', ko: '이용 방법', to: '/#how' },
  { en: 'Sell', ko: '판매하기', to: '/sell/register' },
];

function NavHoverLabel({ en, ko }: { en: string; ko: string }) {
  return (
    <span className="relative inline-flex flex-col overflow-hidden h-5 leading-5">
      <span className="block transition-transform duration-300 ease-out group-hover:-translate-y-5">
        {en}
      </span>
      <span className="block transition-transform duration-300 ease-out group-hover:-translate-y-5">
        {ko}
      </span>
    </span>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLElement>(null);
  const { user, cart, signOut } = useApp();
  const navigate = useNavigate();
  useCatalogVersion(); // 카테고리 데이터가 나중에 도착해도 목록이 갱신되도록 구독만 함 (버튼 자체는 처음부터 동작)
  const freeSlugs = ['ai-skills', 'ai-prompts'];
  const paidCategories = categories.filter((c) => !freeSlugs.includes(c.slug));
  const freeCategories = categories.filter((c) => freeSlugs.includes(c.slug));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      ref={catRef}
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 lg:px-10 transition-all duration-500 ${
        scrolled ? 'pt-3' : 'pt-6'
      }`}
    >
      <div
        className={`mx-auto max-w-7xl liquid-glass rounded-2xl flex flex-col transition-all duration-500 ${
          scrolled ? 'bg-black/80' : ''
        }`}
      >
        {/* Top row */}
        <div className="flex items-center justify-between px-4 py-2">
          <Link to="/" className="flex items-center gap-2.5 group cursor-pointer">
            {/* Custom geometric H logo */}
            <span className="relative w-9 h-9 flex items-center justify-center shrink-0">
              {/* Glow backdrop */}
              <span className="absolute left-[7px] top-[4px] bottom-[4px] w-[5px] rounded-full bg-primary-500 blur-[3px] opacity-50" />
              <span className="absolute right-[7px] top-[4px] bottom-[4px] w-[5px] rounded-full bg-primary-500 blur-[3px] opacity-50" />
              <span className="absolute left-[7px] right-[7px] h-[5px] rounded-full bg-primary-500 blur-[3px] opacity-50 top-1/2 -translate-y-1/2" />
              {/* Left pillar */}
              <span className="absolute left-[7px] top-[4px] bottom-[4px] w-[5px] rounded-full bg-gradient-to-b from-primary-300 to-primary-600" />
              {/* Right pillar */}
              <span className="absolute right-[7px] top-[4px] bottom-[4px] w-[5px] rounded-full bg-gradient-to-b from-primary-300 to-primary-600" />
              {/* Crossbar */}
              <span className="absolute left-[7px] right-[7px] h-[5px] rounded-full bg-gradient-to-r from-primary-400 to-primary-500 top-1/2 -translate-y-1/2" />
            </span>
            <span className="font-display text-xl font-semibold tracking-tight text-white">
              HANDLED
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1 text-sm">
            {/* Category text button */}
            <button
              type="button"
              onMouseEnter={() => setCatOpen(true)}
              onClick={() => setCatOpen((v) => !v)}
              className="group relative px-4 py-2 rounded-lg text-white/70 hover:text-white transition-colors hover-shine whitespace-nowrap cursor-pointer"
            >
              <NavHoverLabel en="Categories" ko="카테고리" />
            </button>

            {navLinks.map((link) =>
              link.to ? (
                <Link
                  key={link.en}
                  to={link.to}
                  className="group relative px-4 py-2 rounded-lg text-white/70 hover:text-white transition-all hover-shine whitespace-nowrap cursor-pointer active:scale-95 active:bg-white/10"
                >
                  <NavHoverLabel en={link.en} ko={link.ko} />
                </Link>
              ) : (
                <a
                  key={link.en}
                  href={link.href}
                  className="group relative px-4 py-2 rounded-lg text-white/70 hover:text-white transition-all hover-shine whitespace-nowrap cursor-pointer active:scale-95 active:bg-white/10"
                >
                  <NavHoverLabel en={link.en} ko={link.ko} />
                </a>
              )
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <Link
              to="/products"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
              aria-label="Search"
            >
              <i className="ri-search-line text-lg"></i>
            </Link>

            <Link
              to="/cart"
              className="relative w-9 h-9 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
              aria-label="Cart"
            >
              <i className="ri-shopping-cart-line text-lg"></i>
              {cart.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 text-[10px] font-bold">
                  {cart.reduce((sum, i) => sum + i.quantity, 0)}
                </span>
              )}
            </Link>

            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-primary-400 hover:text-primary-300 hover:bg-white/5 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-shield-star-line text-base"></i>
                    관리자
                  </Link>
                )}
                <Link
                  to="/mypage"
                  className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 text-xs font-bold">
                    {user.name.charAt(0)}
                  </span>
                  <span className="max-w-[80px] truncate">{user.name}</span>
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    signOut();
                    navigate('/');
                  }}
                  className="hidden sm:inline-flex w-9 h-9 items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                  aria-label="Sign out"
                >
                  <i className="ri-logout-box-r-line text-lg"></i>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="group hidden sm:inline-flex px-4 py-2 rounded-lg text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                >
                  <NavHoverLabel en="Log In" ko="로그인" />
                </Link>
                <Link
                  to="/signup"
                  className="group inline-flex bg-white text-black px-5 py-2 rounded-lg text-sm font-medium hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <NavHoverLabel en="Sign Up" ko="무료 가입" />
                </Link>
              </>
            )}

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-white/15 text-white cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-lg`}></i>
            </button>
          </div>
        </div>

        {/* Desktop category row — appears inside the same box */}
        {catOpen && (
          <div
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
            className="hidden md:flex flex-col items-center gap-1 px-2 pb-2.5 border-t border-white/10"
          >
            <div className="flex items-center justify-center gap-0.5 flex-wrap w-full">
              {paidCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setCatOpen(false)}
                  className="group px-2.5 py-1.5 rounded-md text-xs text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <NavHoverLabel en={cat.enName} ko={cat.name} />
                </Link>
              ))}
            </div>
            <div className="flex items-center justify-center gap-0.5 flex-wrap w-full pt-1.5 border-t border-white/5">
              {freeCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setCatOpen(false)}
                  className="group px-2.5 py-1.5 rounded-md text-xs text-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <NavHoverLabel en={`[Free] ${cat.enName}`} ko={`[무료] ${cat.name}`} />
                </Link>
              ))}
              <Link
                to="/skills/submit"
                onClick={() => setCatOpen(false)}
                className="ml-1 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-primary-500/15 text-primary-400 hover:bg-primary-500/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                무료 등록하기
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden mx-auto max-w-7xl overflow-hidden transition-all duration-500 ${
          mobileOpen ? 'max-h-[85vh] mt-3' : 'max-h-0'
        }`}
      >
        <div className="liquid-glass rounded-2xl p-4 flex flex-col gap-1 bg-black/80 max-h-[85vh] overflow-y-auto">
          <button
            type="button"
            onClick={() => setCatOpen((v) => !v)}
            className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
          >
            <span>Categories</span>
            <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {catOpen && (
            <div className="pl-3 flex flex-col gap-1">
              {paidCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setCatOpen(false);
                    setMobileOpen(false);
                  }}
                  className="rounded-lg px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                >
                  {cat.name}
                </Link>
              ))}
              <div className="mt-1 pt-1 border-t border-white/5 flex flex-col gap-1">
                {freeCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => {
                      setCatOpen(false);
                      setMobileOpen(false);
                    }}
                    className="rounded-lg px-3 py-2 text-sm text-primary-400 hover:text-primary-300 hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    [무료] {cat.name}
                  </Link>
                ))}
                <Link
                  to="/skills/submit"
                  onClick={() => {
                    setCatOpen(false);
                    setMobileOpen(false);
                  }}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-primary-400 hover:text-primary-300 hover:bg-white/5 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line mr-1"></i>
                  무료 등록하기
                </Link>
              </div>
            </div>
          )}

          {navLinks.map((link) =>
            link.to ? (
              <Link
                key={link.en}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-all active:scale-95 active:bg-white/10"
              >
                {link.en}
              </Link>
            ) : (
              <a
                key={link.en}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-all active:scale-95 active:bg-white/10"
              >
                {link.en}
              </a>
            )
          )}
          <Link
            to="/cart"
            onClick={() => setMobileOpen(false)}
            className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors"
          >
            Cart
          </Link>
          {user ? (
            <>
              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm text-primary-400 hover:text-primary-300 hover:bg-white/5 transition-colors"
                >
                  관리자
                </Link>
              )}
              <Link
                to="/mypage"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors"
              >
                My Page
              </Link>
              <button
                type="button"
                onClick={() => {
                  signOut();
                  setMobileOpen(false);
                  navigate('/');
                }}
                className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors text-left cursor-pointer"
              >
                Sign Out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors"
            >
              Log In
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}